import { SocialCommitmentTypeHour } from "@interfaces/commitment";
import { ContractorPackageLineItem } from "@interfaces/contractorPackage";
import {
  Employee,
  EmployeeLineItem,
  EmployeeSocialProcurementFactor,
  EmploymentType,
  Gender,
  NewExistingJobType,
  OccupationType,
} from "@interfaces/employee";
import { ProjectLineItem } from "@interfaces/project";

export const decodeEmployee = (employee: Employee): EmployeeLineItem => {
  return {
    id: employee.contractor_employee_id,
    contractorId: employee.contractor_id,
    employeeName: employee.employee_name,
    postCode: employee.post_code,
    country: employee.country,
    primaryEmployer: employee.primary_employer,
    occupation: employee.occupation,
    socialFactors: employee.social_factors.map((sf) =>
      decodeEmployeeSocialProcurementFactor(sf)
    ),
    created: new Date(employee.created),
    gender: decodeGender(employee.gender),
    occupationType: decodeOccupationType(employee.occupation_type),
    newExistingJob: decodeNewExistingJobType(employee.new_existing_job),
    employmentType: decodeEmploymentType(employee.employment_type),
  };
};

export const encodeEmployee = (employee: EmployeeLineItem): Employee => {
  return {
    contractor_employee_id: employee.id,
    contractor_id: employee.contractorId,
    employee_name: employee.employeeName,
    post_code: employee.postCode,
    country: employee.country,
    primary_employer: employee.primaryEmployer,
    occupation: employee.occupation,
    social_factors: employee.socialFactors.map((sf) =>
      encodeEmployeeSocialProcurementFactor(sf)
    ),
    created: new Date(employee.created),
    gender: encodeGender(employee.gender),
    occupation_type: encodeOccupationType(employee.occupationType),
    new_existing_job: encodeNewExistingJobType(employee.newExistingJob),
    employment_type: encodeEmploymentType(employee.employmentType),
  };
};

export const getEmployeeFactors = (
  contractorPackage: ContractorPackageLineItem,
  employee: EmployeeLineItem
) => {
  const socialFactors: string[] = [];
  if (employee.gender === Gender.Female) {
    const factor = contractorPackage.commitments.find(
      (c) => c.targetName === SocialCommitmentTypeHour.FemaleParticipation
    );
    if (factor) {
      socialFactors.push(factor.id);
    }
  }
  if (
    [
      EmploymentType.Apprentice,
      EmploymentType.Cadet,
      EmploymentType.Trainee,
    ].includes(employee.employmentType)
  ) {
    const factor = contractorPackage.commitments.find(
      (c) => c.targetName === SocialCommitmentTypeHour.MPSGHoursRequirement
    );
    if (factor) {
      socialFactors.push(factor.id);
    }
  }
  if (
    employee.socialFactors.includes(
      EmployeeSocialProcurementFactor.AborignialOrTorresStraitIslander
    )
  ) {
    const factor = contractorPackage.commitments.find(
      (c) =>
        c.targetName ===
        SocialCommitmentTypeHour.AboriginalTorresStraitIslanderParticipation
    );
    if (factor) {
      socialFactors.push(factor.id);
    }
  }
  if (
    employee.socialFactors.includes(
      EmployeeSocialProcurementFactor.AsylumSeekerRefugee
    )
  ) {
    const factor = contractorPackage.commitments.find(
      (c) =>
        c.targetName ===
        SocialCommitmentTypeHour.OpportunitiesForDisadvantagedVictorians
    );
    if (factor) {
      socialFactors.push(factor.id);
    }
  }
  if (
    employee.socialFactors.includes(
      EmployeeSocialProcurementFactor.CulturallyAndLinguisticallyDiverse
    )
  ) {
    const factor = contractorPackage.commitments.find(
      (c) =>
        c.targetName ===
        SocialCommitmentTypeHour.OpportunitiesForDisadvantagedVictorians
    );
    if (factor) {
      socialFactors.push(factor.id);
    }
  }
  if (
    employee.socialFactors.includes(
      EmployeeSocialProcurementFactor.DisengagedYouth
    )
  ) {
    const factor = contractorPackage.commitments.find(
      (c) =>
        c.targetName ===
        SocialCommitmentTypeHour.OpportunitiesForDisadvantagedVictorians
    );
    if (factor) {
      socialFactors.push(factor.id);
    }
  }
  if (
    employee.socialFactors.includes(
      EmployeeSocialProcurementFactor.LongTermUnemployed
    )
  ) {
    const factor = contractorPackage.commitments.find(
      (c) =>
        c.targetName ===
        SocialCommitmentTypeHour.OpportunitiesForDisadvantagedVictorians
    );
    if (factor) {
      socialFactors.push(factor.id);
    }
  }
  if (
    employee.socialFactors.includes(
      EmployeeSocialProcurementFactor.PersonWithDisability
    )
  ) {
    const factor = contractorPackage.commitments.find(
      (c) =>
        c.targetName ===
        SocialCommitmentTypeHour.OpportunitiesForDisadvantagedVictorians
    );
    if (factor) {
      socialFactors.push(factor.id);
    }
  }
  if (
    employee.socialFactors.includes(
      EmployeeSocialProcurementFactor.PersonsAffectedByFamilyViolence
    )
  ) {
    const factor = contractorPackage.commitments.find(
      (c) =>
        c.targetName ===
        SocialCommitmentTypeHour.OpportunitiesForDisadvantagedVictorians
    );
    if (factor) {
      socialFactors.push(factor.id);
    }
  }
  if (
    employee.socialFactors.includes(EmployeeSocialProcurementFactor.Veteran)
  ) {
    const factor = contractorPackage.commitments.find(
      (c) =>
        c.targetName ===
        SocialCommitmentTypeHour.OpportunitiesForDisadvantagedVictorians
    );
    if (factor) {
      socialFactors.push(factor.id);
    }
  }
  return socialFactors;
};

export const decodeEmployeeSocialProcurementFactor = (
  employeeSocialProcurementFactor: string
): EmployeeSocialProcurementFactor => {
  switch (employeeSocialProcurementFactor) {
    default:
    case "employee-social-procurement-factor_local-jobs-first":
      return EmployeeSocialProcurementFactor.LocalJobsFirst;
    case "employee-social-procurement-factor_aborignial-or-torres-strait-islander":
      return EmployeeSocialProcurementFactor.AborignialOrTorresStraitIslander;
    case "employee-social-procurement-factor_asylum-seeker-refugee":
      return EmployeeSocialProcurementFactor.AsylumSeekerRefugee;
    case "employee-social-procurement-factor_disengaged-youth":
      return EmployeeSocialProcurementFactor.DisengagedYouth;
    case "employee-social-procurement-factor_long-term-unemployed":
      return EmployeeSocialProcurementFactor.LongTermUnemployed;
    case "employee-social-procurement-factor_person-with-disability-inclusive-of-mental-illness":
      return EmployeeSocialProcurementFactor.PersonWithDisability;
    case "employee-social-procurement-factor_verteran":
      return EmployeeSocialProcurementFactor.Veteran;
    case "employee-social-procurement-factor_culterually-and-linguistically-diverse":
      return EmployeeSocialProcurementFactor.CulturallyAndLinguisticallyDiverse;
    case "employee-social-procurement-factor_person-affected-by-family-violence":
      return EmployeeSocialProcurementFactor.PersonsAffectedByFamilyViolence;
  }
};

export const encodeEmployeeSocialProcurementFactor = (
  employeeSocialProcurementFactor: EmployeeSocialProcurementFactor
): string => {
  switch (employeeSocialProcurementFactor) {
    case EmployeeSocialProcurementFactor.LocalJobsFirst:
      return "employee-social-procurement-factor_local-jobs-first";
    case EmployeeSocialProcurementFactor.AborignialOrTorresStraitIslander:
      return "employee-social-procurement-factor_aborignial-or-torres-strait-islander";
    case EmployeeSocialProcurementFactor.AsylumSeekerRefugee:
      return "employee-social-procurement-factor_asylum-seeker-refugee";
    case EmployeeSocialProcurementFactor.DisengagedYouth:
      return "employee-social-procurement-factor_disengaged-youth";
    case EmployeeSocialProcurementFactor.LongTermUnemployed:
      return "employee-social-procurement-factor_long-term-unemployed";
    case EmployeeSocialProcurementFactor.PersonWithDisability:
      return "employee-social-procurement-factor_person-with-disability-inclusive-of-mental-illness";
    case EmployeeSocialProcurementFactor.Veteran:
      return "employee-social-procurement-factor_verteran";
    case EmployeeSocialProcurementFactor.CulturallyAndLinguisticallyDiverse:
      return "employee-social-procurement-factor_culterually-and-linguistically-diverse";
    case EmployeeSocialProcurementFactor.PersonsAffectedByFamilyViolence:
      return "employee-social-procurement-factor_person-affected-by-family-violence";
  }
};

export const decodeGender = (gender: string): Gender => {
  switch (gender) {
    default:
    case "gender_male":
      return Gender.Male;
    case "gender_female":
      return Gender.Female;
    case "gender_other":
      return Gender.Other;
  }
};

const encodeGender = (gender: Gender): string => {
  switch (gender) {
    case Gender.Male:
      return "gender_male";
    case Gender.Female:
      return "gender_female";
    case Gender.Other:
      return "gender_other";
  }
};

export const decodeSocialCommitmentTypeHour = (
  commitmentType: string
): SocialCommitmentTypeHour => {
  switch (commitmentType) {
    default:
    case "social-commitment-type_local-jobs-first":
      return SocialCommitmentTypeHour.LocalJobsFirst;
    case "social-commitment-mpsg-hours-requirment":
      return SocialCommitmentTypeHour.MPSGHoursRequirement;
    case "social-commitment-type_opportunities-for-disadvantaged-victorians":
      return SocialCommitmentTypeHour.OpportunitiesForDisadvantagedVictorians;
    case "social-commitment-type_female-participation":
      return SocialCommitmentTypeHour.FemaleParticipation;
    case "social-commitment-type_aboriginal-torres-strait-island-participation":
      return SocialCommitmentTypeHour.AboriginalTorresStraitIslanderParticipation;
  }
};

// const encodeSocialCommitmentTypeHour = (
//   commitmentType: SocialCommitmentTypeHour
// ): string => {
//   switch (commitmentType) {
//     case SocialCommitmentTypeHour.LocalJobsFirst:
//       return "social-commitment-type_local-jobs-first";
//     case SocialCommitmentTypeHour.MPSGHoursRequirement:
//       return "social-commitment-mpsg-hours-requirment";
//     case SocialCommitmentTypeHour.OpportunitiesForDisadvantagedVictorians:
//       return "social-commitment-type_opportunities-for-disadvantaged-victorians";
//     case SocialCommitmentTypeHour.FemaleParticipation:
//       return "social-commitment-type_female-participation";
//     case SocialCommitmentTypeHour.AboriginalTorresStraitIslanderParticipation:
//       return "social-commitment-type_aboriginal-torres-strait-island-participation";
//   }
// };

export const decodeOccupationType = (
  occupationType: string
): OccupationType => {
  switch (occupationType) {
    default:
    case "occupation-type-options_trade-role":
      return OccupationType.TradeRole;
    case "occupation-type-options_non-trade-position":
      return OccupationType.NonTradePosition;
    case "occupation-type-options_management-supervisor-specialist":
      return OccupationType.ManagementSupervisorSpecialistLabourRoles;
    case "occupation-type-options_other":
      return OccupationType.Other;
  }
};

const encodeOccupationType = (occupationType: OccupationType): string => {
  switch (occupationType) {
    default:
    case OccupationType.TradeRole:
      return "occupation-type-options_trade-role";
    case OccupationType.NonTradePosition:
      return "occupation-type-options_non-trade-position";
    case OccupationType.ManagementSupervisorSpecialistLabourRoles:
      return "occupation-type-options_management-supervisor-specialist";
    case OccupationType.Other:
      return "occupation-type-options_other";
  }
};

export const decodeNewExistingJobType = (
  existingJobType: string
): NewExistingJobType => {
  switch (existingJobType) {
    default:
    case "new-existing-job-type_new":
      return NewExistingJobType.New;
    case "new-existing-job-type_existing":
      return NewExistingJobType.Existing;
  }
};

const encodeNewExistingJobType = (
  existingJobType: NewExistingJobType
): string => {
  switch (existingJobType) {
    case NewExistingJobType.New:
      return "new-existing-job-type_new";
    case NewExistingJobType.Existing:
      return "new-existing-job-type_existing";
  }
};

export const decodeEmploymentType = (
  employmentType: string
): EmploymentType => {
  switch (employmentType) {
    default:
    case "employment-type_full-employment":
      return EmploymentType.FullEmployment;
    case "employment-type_apprentice":
      return EmploymentType.Apprentice;
    case "employment-type_trainee":
      return EmploymentType.Trainee;
    case "employment-type_cadet":
      return EmploymentType.Cadet;
  }
};

const encodeEmploymentType = (employmentType: EmploymentType): string => {
  switch (employmentType) {
    case EmploymentType.FullEmployment:
      return "employment-type_full-employment";
    case EmploymentType.Apprentice:
      return "employment-type_apprentice";
    case EmploymentType.Trainee:
      return "employment-type_trainee";
    case EmploymentType.Cadet:
      return "employment-type_cadet";
  }
};
